import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/lib/api';

const API = API_BASE_URL;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const authVersionRef = useRef(0);

  const checkAuth = useCallback(async () => {
    const versionAtStart = authVersionRef.current;
    const isCurrent = () => authVersionRef.current === versionAtStart;

    try {
      const token = localStorage.getItem('token');
      const role = localStorage.getItem('role');
      if (!token) {
        if (isCurrent()) setLoading(false);
        return;
      }
      const res = await axios.get(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      if (isCurrent() && res.data.user) {
        setUser({ ...res.data.user, role: role || res.data.user.role });
      }
    } catch {
      if (isCurrent()) {
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        setUser(null);
      }
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const adminLogin = async (email, password) => {
    const res = await axios.post(`${API}/api/auth/admin/login`, { email, password }, { withCredentials: true });
    const data = res.data;
    authVersionRef.current += 1;
    localStorage.setItem('token', data.token);
    localStorage.setItem('role', 'admin');
    setUser({ ...data.user, role: 'admin' });
    setLoading(false);
    return data;
  };

  const guestLogin = async (email, password) => {
    const res = await axios.post(`${API}/api/auth/guest/login`, { email, password }, { withCredentials: true });
    const data = res.data;
    authVersionRef.current += 1;
    localStorage.setItem('token', data.token);
    localStorage.setItem('role', 'guest');
    setUser({ ...data.user, role: 'guest' });
    setLoading(false);
    return data;
  };

  const guestRegister = async (inviteCode, password) => {
    const res = await axios.post(`${API}/api/auth/guest/register`, {
      invite_code: inviteCode, password
    }, { withCredentials: true });
    const data = res.data;
    authVersionRef.current += 1;
    localStorage.setItem('token', data.token);
    localStorage.setItem('role', 'guest');
    setUser({ ...data.user, role: 'guest' });
    setLoading(false);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch {}
    authVersionRef.current += 1;
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    setUser(null);
    setLoading(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, adminLogin, guestLogin, guestRegister, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// Axios helper with auth token
export function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function apiGet(path) {
  return axios.get(`${API}${path}`, { headers: getAuthHeaders(), withCredentials: true });
}

export function apiPost(path, data) {
  return axios.post(`${API}${path}`, data, { headers: getAuthHeaders(), withCredentials: true });
}

export function apiPut(path, data) {
  return axios.put(`${API}${path}`, data, { headers: getAuthHeaders(), withCredentials: true });
}

export function apiDelete(path) {
  return axios.delete(`${API}${path}`, { headers: getAuthHeaders(), withCredentials: true });
}

export function apiUpload(path, formData) {
  return axios.post(`${API}${path}`, formData, {
    headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
    withCredentials: true,
  });
}
